import { useTranslation } from "react-i18next"
import { ReviewFormWrapper } from "./styles"
import { RiHome5Line } from "react-icons/ri"
import { useFormContext } from "react-hook-form"
import { IFormManualReservation } from ".."
import CalendarAdd from "@assets/svg/calendarAdd.svg?react"
import UserOctagonIcon from "@assets/svg/UserOctagonIcon.svg?react"
import MoneysIcon from "@assets/svg/MoneysIcon.svg?react"
import dayjs from "dayjs"
import localizedFormat from "dayjs/plugin/localizedFormat"
import "dayjs/locale/ar"
import "dayjs/locale/en"
import { useGetManualReservationPriceQuote } from "@services"
dayjs.extend(localizedFormat)

// Whole riyals print bare (460), fractions to the halala (460.50) — matching the emails.
const formatSar = (value: number) =>
	Number.isInteger(value) ? String(value) : value.toFixed(2)

interface IReviewFormProps {
	/** Resolved exactly as the submit payload is, so the quote prices the booking that gets sent. */
	isDirectPaymentAccepted: boolean
}

export default function ReviewForm({ isDirectPaymentAccepted }: IReviewFormProps) {
	/* ---------------------------------- Hooks --------------------------------- */
	const { t, i18n } = useTranslation()

	const { getValues } = useFormContext<IFormManualReservation>()
	const propertyName = getValues("booking_information.propertyName")
	const checkIn = getValues("booking_information.checkIn")
	const checkOut = getValues("booking_information.checkOut")
	const firstName = getValues("gust_information.firstName")
	const lastName = getValues("gust_information.lastName")
	const guestPhoneNumber = getValues("gust_information.guestPhoneNumber")
	const nightPrice = getValues("pricing_information.nightPrice")
	const totalPrice = Number(getValues("pricing_information.totalPrice"))
	const numberOfNights =
		checkIn && checkOut ? dayjs(checkOut).diff(dayjs(checkIn), "days") : 0

	// MS-618 — the host's price is not what the guest pays: with direct payment the backend adds
	// VAT (and, pre-launch, Msool commission). Ask it for the breakdown instead of guessing, so this
	// total always matches the reservations table and the guest email.
	const { data: quoteResponse, isLoading: isQuoteLoading } =
		useGetManualReservationPriceQuote(
			{ totalPrice, isDirectPaymentAccepted },
			{ enabled: totalPrice > 0 }
		)
	const quote = quoteResponse?.result
	const hasExtras = !!quote && (quote.vat > 0 || quote.commission > 0)

	return (
		<ReviewFormWrapper>
			<div className='containerItem'>
				<p>{t("MANUALRESERVATION.REVIEWTHEDATAANDCONFIRMTHERESERVATION")}</p>
			</div>
			<div className='containerItem'>
				<div className='containerSVG'>
					<RiHome5Line />
				</div>
				<p>{t("MANUALRESERVATION.PROPERTYINFORMATION")}</p>
				<p>{propertyName}</p>
			</div>
			<div className='containerItem'>
				<div className='containerSVG'>
					<CalendarAdd />
				</div>
				<p>{t("MANUALRESERVATION.TIMEANDDATEOFRESERVATION")}</p>
				<p>
					{t("MANUALRESERVATION.BOOKINGSTARTDATE")} :{" "}
					{checkIn
						? dayjs(checkIn).locale(i18n.language).format("YYYY MMM DD")
						: ""}
				</p>
				<p>
					{t("MANUALRESERVATION.BOOKINGENDDATE")} :{" "}
					{checkOut
						? dayjs(checkOut).locale(i18n.language).format("YYYY MMM DD")
						: ""}
				</p>
			</div>
			<div className='containerItem'>
				<div className='containerSVG'>
					<UserOctagonIcon />
				</div>
				<p>{t("MANUALRESERVATION.GUESTDATA")}</p>
				<p>
					{firstName} {lastName}
				</p>
				<p>{guestPhoneNumber}</p>
			</div>
			<div className='containerItem'>
				<div className='containerSVG'>
					<MoneysIcon />
				</div>
				<p>{t("COMMON.BOOKING_PRICE_DATA")}</p>
				<p>
					{t("MANUALRESERVATION.NIGHTPRICE")}
					{" : "}
					<span>
						{nightPrice} {t("MANUALRESERVATION.SAR")}
					</span>
				</p>
				{numberOfNights > 0 && (
					<p>
						{t("MANUALRESERVATION.NUMBEROFNIGHTS")}
						{" : "}
						<span>{numberOfNights}</span>
					</p>
				)}
				{hasExtras && (
					<>
						<p>
							{t("MANUALRESERVATION.SUBTOTALBEFOREVAT")}
							{" : "}
							<span>
								{formatSar(quote.hostSubTotal)} {t("MANUALRESERVATION.SAR")}
							</span>
						</p>
						{quote.commission > 0 && (
							<p>
								{t("MANUALRESERVATION.MSOOLCOMMISSION")}
								{" : "}
								<span>
									{formatSar(quote.commission)} {t("MANUALRESERVATION.SAR")}
								</span>
							</p>
						)}
						{quote.vat > 0 && (
							<p>
								{t("MANUALRESERVATION.VATWITHRATE", {
									rate: Math.round(quote.vatRate * 10000) / 100,
								})}
								{" : "}
								<span>
									{formatSar(quote.vat)} {t("MANUALRESERVATION.SAR")}
								</span>
							</p>
						)}
					</>
				)}
				<p>
					{t("MANUALRESERVATION.TOTALPRICEOFRESERVATION")}
					{" : "}
					<span>
						{isQuoteLoading
							? "..."
							: formatSar(quote?.guestTotal ?? totalPrice)}{" "}
						{t("MANUALRESERVATION.SAR")}
					</span>
				</p>
			</div>
		</ReviewFormWrapper>
	)
}
